import { AlertTriangle, BadgePercent, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { HistorySection } from '@/features/audit-log';
import { usePermissions } from '@/features/auth';
import { useMarketTimeZone, useSelectedCountry } from '@/features/country-scope';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge, type StatusVariant } from '@/shared/components/status-badge';
import { formatCurrency, formatDateTime } from '@/shared/lib/format';

import {
  useGlobalDiscounts,
  useSetGlobalDiscountActive,
  useSetGlobalDiscountBanner,
} from '../api/global-discount.queries';
import { BannerPreview } from '../components/banner-preview';
import { GlobalDiscountDialog } from '../components/global-discount-dialog';
import type { GlobalDiscount, GlobalDiscountStatus } from '../types/global-discount.types';

const GLOBAL_DISCOUNT_STATUS: Record<
  GlobalDiscountStatus,
  { label: string; variant: StatusVariant }
> = {
  live: { label: 'Running now', variant: 'success' },
  scheduled: { label: 'Scheduled', variant: 'info' },
  ended: { label: 'Ended', variant: 'neutral' },
  off: { label: 'Switched off', variant: 'neutral' },
};

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; discount: GlobalDiscount }
  | { kind: 'active'; discount: GlobalDiscount }
  | null;

function value(d: GlobalDiscount) {
  return d.maxDiscountAmount === null
    ? `${d.percentOff}% off every voucher`
    : `${d.percentOff}% off every voucher, max ${formatCurrency(d.maxDiscountAmount, d.currency ?? undefined, { precise: true })} per item`;
}

/**
 * Marketing › Global discount: a percent off every voucher of the selected country for a period,
 * announced by a storefront banner (GLOBAL_DISCOUNT_PLAN.md). Read with coupons.read, changed with
 * coupons.manage. Only one sale per country per period; coupons can't be used while one runs.
 */
export function GlobalDiscountPage() {
  const { country } = useSelectedCountry();
  const zone = useMarketTimeZone();
  const { can } = usePermissions();
  const canManage = can('coupons.manage');
  const discounts = useGlobalDiscounts(country?.id);
  const setActive = useSetGlobalDiscountActive();
  const setBanner = useSetGlobalDiscountBanner();
  const [open, setOpen] = useState<Open>(null);
  const countryName = country ? (country.nameEn ?? country.code ?? `#${country.id}`) : '';
  const currency = discounts.data?.[0]?.currency ?? null;
  const target = open?.kind === 'active' ? open.discount : null;

  const current =
    discounts.data?.filter((d) => d.status === 'live' || d.status === 'scheduled') ?? [];
  const past = discounts.data?.filter((d) => d.status === 'ended' || d.status === 'off') ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Global discount"
        description="A percent off every voucher in the country for a period, with a banner on the storefront. One global discount per country per period; coupons can't be combined with it."
        actions={
          canManage && country ? (
            <Button size="sm" onClick={() => setOpen({ kind: 'create' })}>
              <Plus className="size-3.5" aria-hidden />
              New global discount
            </Button>
          ) : null
        }
      />
      {!country ? (
        <EmptyState
          icon={BadgePercent}
          title="Choose a country"
          description="Global discounts are set per country."
        />
      ) : discounts.isLoading ? (
        <Skeleton className="h-40" />
      ) : discounts.error || !discounts.data ? (
        <ErrorState error={discounts.error} onRetry={() => void discounts.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          {current.length === 0 ? (
            <EmptyState
              icon={BadgePercent}
              title="No global discount running or scheduled"
              description={`Customers in ${countryName} pay normal prices.`}
            />
          ) : (
            current.map((d) => (
              <Card key={d.id} role="region" aria-label={`Global discount ${d.percentOff}%`}>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-sm">
                      {value(d)}
                      <StatusBadge variant={GLOBAL_DISCOUNT_STATUS[d.status].variant}>
                        {GLOBAL_DISCOUNT_STATUS[d.status].label}
                      </StatusBadge>
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {formatDateTime(d.startsAt, zone)} → {formatDateTime(d.endsAt, zone)} ·{' '}
                      {d.orderCount} orders ·{' '}
                      {formatCurrency(d.discountTotal, d.currency ?? undefined, { precise: true })}{' '}
                      discounted
                    </CardDescription>
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpen({ kind: 'edit', discount: d })}
                        aria-label={`Edit global discount ${d.percentOff}%`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpen({ kind: 'active', discount: d })}
                        aria-label={`Switch off global discount ${d.percentOff}%`}
                      >
                        Switch off
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="flex flex-col gap-3 text-xs">
                  {d.marginWarning && (
                    <p className="text-status-warning flex items-start gap-1">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                      {d.marginWarning}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`banner-${d.id}`}
                      checked={d.bannerEnabled}
                      disabled={!canManage || setBanner.isPending}
                      onCheckedChange={(on) => setBanner.mutate({ id: d.id, on })}
                      aria-label="Show the banner on the storefront"
                    />
                    <label htmlFor={`banner-${d.id}`}>
                      Banner{' '}
                      {d.bannerShowing
                        ? 'is on the storefront now'
                        : d.bannerEnabled
                          ? 'shows when the sale starts'
                          : 'is off'}
                    </label>
                  </div>
                  <BannerPreview
                    titleEn={d.bannerTitleEn ?? ''}
                    textEn={d.bannerTextEn ?? ''}
                    titleAr={d.bannerTitleAr ?? ''}
                    textAr={d.bannerTextAr ?? ''}
                  />
                  <HistorySection
                    entityType="GlobalDiscount"
                    entityId={String(d.id)}
                    title="Changes"
                    timeZone={zone}
                    collapsible
                  />
                </CardContent>
              </Card>
            ))
          )}

          {past.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Earlier global discounts</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-border divide-y text-xs">
                  {past.map((d) => (
                    <li
                      key={d.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2"
                    >
                      <span>
                        <span className="font-medium">{value(d)}</span>
                        <span className="text-muted-foreground block">
                          {formatDateTime(d.startsAt, zone)} → {formatDateTime(d.endsAt, zone)} ·{' '}
                          {d.orderCount} orders
                        </span>
                      </span>
                      <span className="flex items-center gap-2">
                        <StatusBadge variant={GLOBAL_DISCOUNT_STATUS[d.status].variant}>
                          {GLOBAL_DISCOUNT_STATUS[d.status].label}
                        </StatusBadge>
                        {canManage && d.status === 'off' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setOpen({ kind: 'active', discount: d })}
                            aria-label={`Switch on global discount ${d.percentOff}%`}
                          >
                            Switch on
                          </Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {country && open?.kind === 'create' && (
        <GlobalDiscountDialog
          countryId={country.id}
          countryName={countryName}
          currency={currency}
          onClose={() => setOpen(null)}
        />
      )}
      {country && open?.kind === 'edit' && (
        <GlobalDiscountDialog
          countryId={country.id}
          countryName={countryName}
          currency={open.discount.currency}
          discount={open.discount}
          onClose={() => setOpen(null)}
        />
      )}
      <ConfirmDialog
        open={target !== null}
        onOpenChange={(isOpen) => !isOpen && setOpen(null)}
        title={
          target?.isActive
            ? 'Switch off this global discount?'
            : 'Switch this global discount back on?'
        }
        description={
          target?.isActive
            ? 'Customers pay normal prices from now on and the banner disappears. Payments already started keep their discounted price.'
            : 'It applies again within its dates — unless another global discount covers the period.'
        }
        confirmLabel={target?.isActive ? 'Switch off' : 'Switch on'}
        variant={target?.isActive ? 'destructive' : 'default'}
        isPending={setActive.isPending}
        onConfirm={() =>
          target &&
          setActive.mutate(
            { id: target.id, active: !target.isActive },
            { onSuccess: () => setOpen(null) },
          )
        }
      />
    </PageContainer>
  );
}
